/**
 * CX Trust Badges: mobile carousel, auto-advance, reduced-motion aware. Position-cached for minimal reflow.
 * Single global resize listener notifies all tracks to avoid N listeners for N strips.
 *
 * Carousel viewport must match assets/cx-marquee-trust-badges.css (@media max-width: 1099px).
 */
(function () {
  const SEL = '[data-trust-badges-track]';
  /** Upper bound (px) for one-slide carousel — keep in sync with .trust-badges-strip CSS breakpoints */
  const CAROUSEL_MAX_WIDTH_PX = 1099;
  const state = new WeakMap();
  const resizeRegistry = new Set();
  let globalResizeBound = false;
  let reducedMotion = false;
  let reducedMotionBound = false;

  function onGlobalResize() {
    resizeRegistry.forEach((track) => {
      const s = state.get(track);
      if (s && typeof s.onResize === 'function') s.onResize();
    });
  }

  function bindGlobalResize() {
    if (globalResizeBound) return;
    globalResizeBound = true;
    window.addEventListener('resize', onGlobalResize, { passive: true });
  }

  function prefersReducedMotion() {
    if (!reducedMotionBound && typeof window !== 'undefined' && window.matchMedia) {
      reducedMotionBound = true;
      const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
      reducedMotion = !!mql.matches;
      mql.addEventListener?.('change', (e) => { reducedMotion = !!e.matches; });
    }
    return reducedMotion;
  }

  function stop(track) {
    const s = state.get(track);
    if (s?.timer) clearInterval(s.timer);
    if (s) s.timer = 0;
  }

  function destroy(track) {
    stop(track);
    state.get(track)?.cleanup?.();
    state.delete(track);
  }

  function initTrack(track) {
    if (track.dataset.cxTrustBadgesInit === 'true') return;
    track.dataset.cxTrustBadgesInit = 'true';
    if (window.Shopify?.designMode) {
      track.classList.add('trust-badges-strip__track--static');
      return;
    }

    const items = Array.from(track.children).filter((c) => c instanceof HTMLElement);
    const delayBase = (Number.parseInt(track.dataset.autoAdvanceDelay || '5', 10) || 5) * 1000;
    const delayDesktop = track.dataset.autoAdvanceDelayDesktop ? (Number.parseInt(track.dataset.autoAdvanceDelayDesktop, 10) || 5) * 1000 : delayBase;
    const isCarouselViewport = () => window.innerWidth <= CAROUSEL_MAX_WIDTH_PX;
    const getDelay = () => (isCarouselViewport() ? delayBase : delayDesktop);

    let index = 0;
    let positions = [];

    function setStatic() {
      track.classList.add('trust-badges-strip__track--static');
      track.style.transform = '';
      track.style.transition = '';
      stop(track);
    }

    function getSlotWidth() {
      return isCarouselViewport() ? window.innerWidth : (track.parentElement?.clientWidth ?? window.innerWidth);
    }

    function goToSlide(i) {
      if (!items.length) return;
      index = (i % items.length + items.length) % items.length;
      if (positions.length !== items.length) {
        const w = getSlotWidth();
        positions = items.map((_, i) => -i * w);
      }
      track.style.transform = `translateX(${positions[index]}px)`;
      track.style.transition = 'transform 0.5s ease-in-out';
      items.forEach((el, j) => el.classList.toggle('trust-badges-strip__badge--active', j === index));
    }

    function startAdvance() {
      stop(track);
      if (prefersReducedMotion() || items.length <= 1) return;
      const s = state.get(track);
      const delayMs = getDelay();
      s.timer = setInterval(() => goToSlide(index + 1), delayMs);
    }

    function onResize() {
      const s = state.get(track);
      clearTimeout(s?.resizeTid);
      s.resizeTid = setTimeout(() => {
        positions = [];
        if (isCarouselViewport() && !prefersReducedMotion() && items.length > 1) {
          track.classList.remove('trust-badges-strip__track--static');
          goToSlide(index);
          startAdvance();
        } else {
          setStatic();
          index = 0;
        }
      }, 100);
    }

    const onEnter = () => stop(track);
    const onLeave = () => startAdvance();
    const onTouchEnd = () => setTimeout(startAdvance, 1000);
    track.addEventListener('mouseenter', onEnter);
    track.addEventListener('mouseleave', onLeave);
    track.addEventListener('touchstart', onEnter, { passive: true });
    track.addEventListener('touchend', onTouchEnd, { passive: true });
    resizeRegistry.add(track);
    bindGlobalResize();

    const s = {
      timer: 0,
      resizeTid: 0,
      onResize,
      cleanup() {
        clearTimeout(this.resizeTid);
        resizeRegistry.delete(track);
        track.removeEventListener('mouseenter', onEnter);
        track.removeEventListener('mouseleave', onLeave);
        track.removeEventListener('touchstart', onEnter);
        track.removeEventListener('touchend', onTouchEnd);
      }
    };
    state.set(track, s);

    if (items.length <= 1 || prefersReducedMotion() || !isCarouselViewport()) {
      setStatic();
      return;
    }

    function boot() {
      if (items[0]?.offsetWidth === 0) {
        requestAnimationFrame(boot);
        return;
      }
      track.classList.remove('trust-badges-strip__track--static');
      goToSlide(0);
      startAdvance();
    }

    const runBoot = () => setTimeout(boot, 100);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', runBoot, { once: true });
    else runBoot();
  }

  function findTracks(root) {
    if (!root?.querySelectorAll) return [];
    const list = root.matches?.(SEL) ? [root] : [];
    return list.concat(Array.from(root.querySelectorAll(SEL)));
  }

  window.CX = window.CX || {};
  window.CX.initTrustBadges = (root) => findTracks(root || document).forEach((t) => t instanceof HTMLElement && initTrack(t));
  window.CX.destroyTrustBadges = (root) => { if (root instanceof Element) findTracks(root).forEach((t) => t instanceof HTMLElement && destroy(t)); };

  const run = (r) => window.CX.initTrustBadges(r || document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  else setTimeout(() => run(document), 0);
  document.addEventListener('shopify:section:load', (e) => run(e?.target instanceof Element ? e.target : document));
  document.addEventListener('shopify:section:unload', (e) => { if (e?.target instanceof Element) window.CX.destroyTrustBadges(e.target); });
  document.addEventListener('shopify:section:reorder', () => run(document));
  document.addEventListener('shopify:block:select', () => run(document));
})();
